import { SIGN_OUT_REASONS } from '@wheely/web-auth-lib/core';
import type { AuthClient, AuthError } from '@wheely/web-auth-lib/core';

import { createFlowJournal } from '@/services/flowJournal';

import { getAuthMode } from './mode';

/** One per page load: auth steps of the whole visit share its attempt id. */
const authJournal = createFlowJournal('auth');

const describeAuthError = (error: AuthError) => ({
  code: error.code,
  kind: error.kind,
  status: error.status,
  retryable: error.retryable,
});

/**
 * Journals what the V3 client does, so a session lost mid-visit shows up in
 * the steps of whatever page it broke. A sign-out the visitor did not ask for
 * is reported to Sentry on its own.
 */
const journalAuthClient = (client: Pick<AuthClient, 'getState' | 'on'>) => {
  let status = client.getState().status;

  client.on('stateChanged', state => {
    if (state.status !== status) {
      authJournal.step('status', { from: status, to: state.status });
      status = state.status;
    }
  });
  client.on('sessionRenewed', () => authJournal.step('session_renewed'));
  client.on('error', (error, operation) =>
    authJournal.step('error', { operation, ...describeAuthError(error) }),
  );
  client.on('signedOut', reason => {
    const data = { reason, auth_mode: getAuthMode(), path: window.location.pathname };

    if (reason === SIGN_OUT_REASONS.USER) {
      authJournal.step('signed_out', data);
    } else {
      authJournal.report('signed_out', data);
    }
  });
};

export { authJournal, journalAuthClient };
